import { useEffect } from 'react';
import { contact } from '../../data/site';
import type { PageMetadata } from '../../types/pageContent';
const productionOrigin = 'https://www.motorcitytruckcollision.com';
const businessId = `${productionOrigin}/#body-shop`;
const business = {
  '@type': 'AutoBodyShop',
  '@id': businessId,
  name: contact.name,
  url: `${productionOrigin}/`,
  telephone: '+1-248-986-8000',
  address: {
    '@type': 'PostalAddress',
    streetAddress: contact.street,
    addressLocality: 'Brighton',
    addressRegion: 'MI',
    postalCode: '48116',
    addressCountry: 'US'
  }
};
export function PageHead({
  metadata
}: {
  metadata: PageMetadata;
}) {
  useEffect(() => {
    document.title = metadata.title;
    const meta = (attribute: 'name' | 'property', key: string, value: string) => {
      const matches = [...document.head.querySelectorAll<HTMLMetaElement>(`meta[${attribute}="${key}"]`)];
      const element = matches.shift() || document.createElement('meta');
      matches.forEach(node => node.remove());
      element.setAttribute(attribute, key);
      element.content = value;
      if (!element.parentNode) document.head.appendChild(element);
    };
    meta('name', 'description', metadata.description);
    meta('name', 'robots', 'noindex, follow');
    meta('property', 'og:title', metadata.title);
    meta('property', 'og:description', metadata.description);
    meta('property', 'og:type', 'website');
    document.head.querySelectorAll('[data-page-schema]').forEach(node => node.remove());
    document.head.querySelectorAll('link[rel="canonical"]').forEach(node => node.remove());
    document.head.querySelectorAll('meta[property="og:image"], meta[property="og:url"]').forEach(node => node.remove());
    if (!metadata.path) return;
    const canonical = productionOrigin + metadata.path;
    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = canonical;
    document.head.appendChild(link);
    meta('property', 'og:url', canonical);
    if (metadata.image) meta('property', 'og:image', metadata.image.src);
    const graph: Record<string, unknown>[] = [business, {
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: metadata.title,
      description: metadata.description,
      about: {
        '@id': businessId
      }
    }];
    if (metadata.kind === 'service') graph.push({
      '@type': 'Service',
      '@id': `${canonical}#service`,
      name: metadata.title.split(' | ')[0],
      description: metadata.description,
      url: canonical,
      provider: {
        '@id': businessId
      }
    });
    if (metadata.breadcrumbs?.length) graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumbs`,
      itemListElement: metadata.breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: productionOrigin + item.path
      }))
    });
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.pageSchema = 'true';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': graph
    });
    document.head.appendChild(script);
    return () => {
      script.remove();
      link.remove();
    };
  }, [metadata]);
  return null;
}